import React, { useState, useRef, useEffect } from 'react';
import Draggable from 'react-draggable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulb, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useT } from '../i18n/langContext.js';

const exec = (cmd) => document.execCommand(cmd, false, null);

// Allowlist sanitizer (no dependency): keep only basic formatting tags, strip
// ALL attributes (drops on*, style, href, etc.) so stored/rendered notes are safe.
const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'UL', 'OL', 'LI', 'BR', 'P', 'DIV', 'SPAN']);
export function sanitizeHtml(html) {
  // Parse in an inert document: no script execution, no img src fetch / onerror during sanitize.
  const docp = new DOMParser().parseFromString(html || '', 'text/html');
  const walk = (node) => {
    let changed = false;
    Array.from(node.children).forEach((el) => {
      if (!ALLOWED_TAGS.has(el.tagName)) {
        el.replaceWith(...el.childNodes); // unwrap disallowed element, keep its text
        changed = true;
      } else {
        Array.from(el.attributes).forEach((a) => el.removeAttribute(a.name));
        walk(el);
      }
    });
    // Children promoted by an unwrap are now siblings; re-walk so they get sanitized too.
    if (changed) walk(node);
  };
  walk(docp.body);
  return docp.body.innerHTML;
}

// One brainstorm entry: a referenced card + rich-text notes.
// Uncontrolled contentEditable (init once via ref) so typing never resets the caret.
function BrainstormEntry({ entry, cards, onCard, onHtml, onRemove }) {
  const t = useT();
  const ref = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (entry.html || '')) {
      ref.current.innerHTML = entry.html || '';
    }
    // init content once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bs-entry">
      <div className="bs-entry__head">
        {entry.card ? (
          <button className="bs-card-chip" onClick={() => setPickerOpen((v) => !v)} title={t('chat.brainstormPanel.changeCardTitle')}>
            {entry.card.image && <img src={entry.card.image} alt="" />}
            <span>{entry.card.label}</span>
          </button>
        ) : (
          <button className="bs-card-add" onClick={() => setPickerOpen((v) => !v)}>{t('chat.brainstormPanel.addCardBtn')}</button>
        )}
        <button className="bs-del" title={t('chat.brainstormPanel.deleteNoteTitle')} onClick={onRemove}>✕</button>
      </div>

      {pickerOpen && (
        <div className="bs-picker">
          {cards.length === 0 && <div className="bs-picker__empty">{t('chat.brainstormPanel.noCards')}</div>}
          {cards.map((c, i) => (
            <button key={i} className="bs-picker__item" onClick={() => { onCard({ label: c.label, image: c.image, group: c.group }); setPickerOpen(false); }}>
              {c.image && <img src={c.image} alt="" />}
              <span>{c.label}</span>
            </button>
          ))}
        </div>
      )}

      <div className="bs-toolbar">
        <button onMouseDown={(e) => { e.preventDefault(); exec('bold'); }} title={t('chat.brainstormPanel.boldTitle')}><b>B</b></button>
        <button onMouseDown={(e) => { e.preventDefault(); exec('italic'); }} title={t('chat.brainstormPanel.italicTitle')}><i>I</i></button>
        <button onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }} title={t('chat.brainstormPanel.listTitle')}>≡</button>
      </div>
      <div
        ref={ref}
        className="bs-editor"
        contentEditable
        suppressContentEditableWarning
        onInput={() => onHtml(sanitizeHtml(ref.current.innerHTML))}
        data-placeholder={t('chat.brainstormPanel.notesPlaceholder')}
      />
    </div>
  );
}

// Brainstorm entries list (card + rich notes). Shared by the floating widget.
export function BrainstormPanel({ brainstorm, setBrainstorm, cards }) {
  const t = useT();
  const addEntry = () => setBrainstorm((prev) => [...prev, { id: `bs-${Date.now()}`, card: null, html: '' }]);
  const removeEntry = (id) => setBrainstorm((prev) => prev.filter((e) => e.id !== id));
  const setCard = (id, card) => setBrainstorm((prev) => prev.map((e) => (e.id === id ? { ...e, card } : e)));
  const setHtml = (id, html) => setBrainstorm((prev) => prev.map((e) => (e.id === id ? { ...e, html } : e)));

  return (
    <div className="brainstorm-panel">
      {brainstorm.length === 0 && (
        <div className="bs-intro">{t('chat.brainstormPanel.intro')}</div>
      )}
      {brainstorm.map((entry) => (
        <BrainstormEntry
          key={entry.id}
          entry={entry}
          cards={cards}
          onCard={(card) => setCard(entry.id, card)}
          onHtml={(html) => setHtml(entry.id, html)}
          onRemove={() => removeEntry(entry.id)}
        />
      ))}
      <button className="bs-add" onClick={addEntry}>{t('chat.brainstormPanel.addNoteBtn')}</button>
    </div>
  );
}

// Independent floating widget: draggable + collapsible, always on the canvas.
export default function BrainstormWidget({ brainstorm, setBrainstorm, cards }) {
  const t = useT();
  const [open, setOpen] = useState(false); // default collapsed
  const dragRef = useRef(null);
  const count = brainstorm.length;

  return (
    <Draggable handle=".bs-widget__header" cancel=".bs-widget__caret" bounds="parent" nodeRef={dragRef}>
      <div ref={dragRef} className={`bs-widget${open ? '' : ' is-collapsed'}`} style={{ zIndex: 1000 }}>
        <div className="bs-widget__header" onClick={() => setOpen((v) => !v)}>
          <span className="bs-widget__id">
            <FontAwesomeIcon className="bs-widget__icon" icon={faLightbulb} />
            <span className="bs-widget__title">
              {t('chat.brainstormPanel.widgetTitle')}
              {count > 0 && <span className="bs-widget__count">{count}</span>}
            </span>
          </span>
          <span className="bs-widget__caret">
            <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
          </span>
        </div>
        {open && (
          <div className="bs-widget__body">
            <BrainstormPanel brainstorm={brainstorm} setBrainstorm={setBrainstorm} cards={cards} />
          </div>
        )}
      </div>
    </Draggable>
  );
}
